import { useContext, useEffect, useMemo, useRef } from 'react'

import { trackSearchCalendarEventOverlayImpression } from 'analytics/snowplow/events/searchCalendarEventOverlayEvents'
import type { CalendarVisibleMonths } from 'components/Common/Calendar/calendarFocusMonth'
import { Experiments } from 'constants/experiments'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import useExperiment from 'hooks/Experiments/useExperiment'
import useSearchCalendarEvents from 'hooks/Search/useSearchCalendarEvents'
import {
  countHighlightedDatesInMonth,
  eventForMonthKey,
  eventsForPlaceIds,
  eventsIntersectingMonths,
  eventsNearMonths,
  offsetMonthKey,
  selectPrimaryEvent,
} from 'lib/search/searchCalendarEvents'
import { getPlaceIdsFromSearchItem } from 'lib/search/searchedPlaces'

function useSearchCalendarEventOverlay(visibleMonths?: CalendarVisibleMonths, isOpen = false) {
  const { searchItem } = useContext(GlobalSearchStateContext)
  const impressedMonthsRef = useRef(new Set<string>())

  /**
   * Keyed on `isOpen` only — never on the experiment variation. Both arms have
   * to fetch at the same moment under the same conditions, or treatment's
   * eligible population would differ from control's and bias the apply ratio.
   */
  const events = useSearchCalendarEvents(isOpen)

  const placeIds = useMemo(
    () => searchItem ? getPlaceIdsFromSearchItem(searchItem) : [],
    [searchItem],
  )
  const matchingEvents = useMemo(
    () => eventsForPlaceIds(events, placeIds),
    [events, placeIds],
  )
  /** Exposure population — one month either side of an event month still counts. */
  const canActivate = useMemo(
    () => eventsNearMonths(matchingEvents, visibleMonths).length > 0,
    [matchingEvents, visibleMonths],
  )
  /** A single event per visible 2-month block, and only where dates actually land. */
  const primaryEvent = useMemo(
    () => selectPrimaryEvent(eventsIntersectingMonths(matchingEvents, visibleMonths)),
    [matchingEvents, visibleMonths],
  )

  const { isAnyVariant, isTurnedOn } = useExperiment(Experiments.searchCalendarEventOverlay, { canActivate })
  const showOverlay = !!primaryEvent && isAnyVariant

  /**
   * Events in the months paging would newly reveal — one step either side. Keyed on
   * the month that becomes visible, not the whole adjacent viewport, so a hint never
   * points at an event already painted on screen.
   */
  const adjacentEvents = useMemo(() => {
    if (!visibleMonths?.left) return {}

    const previousMonthKey = offsetMonthKey(visibleMonths.left, -1)
    const nextMonthKey = offsetMonthKey(visibleMonths.right ?? visibleMonths.left, 1)

    return {
      previousMonthEvent: eventForMonthKey(matchingEvents, previousMonthKey),
      previousMonthKey,
      nextMonthEvent: eventForMonthKey(matchingEvents, nextMonthKey),
      nextMonthKey,
    }
  }, [matchingEvents, visibleMonths])

  /**
   * How a suggestion surfaced, kept apart because they are different strength of
   * evidence — see `maybeTrackSearchCalendarEventOverlayApply`.
   *
   * Both are computed for either arm. Control paints and hints nothing, but it needs
   * the same denominators, otherwise treatment's larger eligible population would
   * bias the apply ratio between them.
   */
  const hadVisibleSuggestion = !!primaryEvent
  const hadHintedSuggestion = !hadVisibleSuggestion && (
    !!adjacentEvents.previousMonthEvent || !!adjacentEvents.nextMonthEvent
  )

  const impressableMonthKeys = useMemo(() => {
    if (!primaryEvent) return []

    return [visibleMonths?.left, visibleMonths?.right]
      .filter((monthKey): monthKey is string => !!monthKey)
      .filter(monthKey => countHighlightedDatesInMonth(primaryEvent, monthKey) > 0)
  }, [primaryEvent, visibleMonths])

  useEffect(() => {
    if (!isOpen) {
      impressedMonthsRef.current.clear()
      return
    }

    if (!isTurnedOn || !primaryEvent) return

    for (const monthKey of impressableMonthKeys) {
      const impressionKey = `${monthKey}:${primaryEvent.id}`
      if (impressedMonthsRef.current.has(impressionKey)) continue

      impressedMonthsRef.current.add(impressionKey)
      trackSearchCalendarEventOverlayImpression({
        monthKey,
        event: primaryEvent,
        placeIds,
        isHighlighted: isAnyVariant,
      })
    }
  }, [impressableMonthKeys, isAnyVariant, isOpen, isTurnedOn, placeIds, primaryEvent])

  return useMemo(() => ({
    canActivate,
    /** Treatment variation — gates anything the overlay draws, hints included. */
    isAnyVariant,
    isTurnedOn,
    matchingEvents,
    placeIds,
    hadHintedSuggestion,
    hadVisibleSuggestion,
    primaryEvent,
    showOverlay,
    ...adjacentEvents,
  }), [
    adjacentEvents,
    canActivate,
    isAnyVariant,
    hadHintedSuggestion,
    hadVisibleSuggestion,
    isTurnedOn,
    matchingEvents,
    placeIds,
    primaryEvent,
    showOverlay,
  ])
}

export default useSearchCalendarEventOverlay
